import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LoginComponent } from '@app/account/login/login.component';
import { ResetPasswordComponent } from '@app/account/reset-password/reset-password.component';
import { ForgotPasswordComponent } from '@app/account/forgot-password/forgot-password.component';
import { IsAuthenticatedGuardChild } from '@app/account/is-authenticated.guard';
import { NewPasswordRequiredComponent } from '@app/account/new-password-required/new-password-required.component';
import { ViewFrameComponent } from '@scaffold/routes/view-frame/view-frame.component';
import { EditFrameComponent } from '@scaffold/routes/edit-frame/edit-frame.component';
import { ModelTypeListComponent } from './data-model/type-list/type-list.component';
import { ModelDetailsComponent } from './data-model/model-details/model-details.component';
import { KeyItemsComponent } from './data-model/key-items/key-items.component';
import { DataListComponent } from '@scaffold/routes/data-list/data-list.component';
import { VerifyPhoneAndEmailComponent } from './account/verify-phone-and-email/verify-phone-and-email.component';
import { DataModelPrefsComponent } from './data-model/data-model-prefs/data-model-prefs.component';
import { PendingChangesGuard } from '@app/shared/pending-changes.guard';
import { LtiCallbackComponent } from './utility/lti-callback/lti-callback.component';
import { LtiFrameComponent } from './utility/lti-frame/lti-frame.component';
import { RolePermissionsGuard } from './account/role-permissions.guard';
// import { ChangelogComponent } from './changes/changelog/changelog.component';
import { CourseSelectComponent } from '@scaffold/routes/course-select/course-select.component';
import { DocumentSelectComponent } from '@scaffold/routes/document-select/document-select.component';
import { CourseCreateComponent } from '@scaffold/routes/course-create/course-create.component';
import { LandingPageComponent } from '@scaffold/routes/landing-page/landing-page.component';
import { NewDataComponent } from '@scaffold/routes/new-data/new-data.component';
import { SecurityComponent } from './security/security.component';
import { AccountSelectComponent } from '@scaffold/routes/account-select/account-select.component';
import { RouteGuardService } from '@services/route-guard.service';
import { HomeGuard } from '@services/home.guard';
import { SnowflakeAccessComponent } from '@scaffold/routes/snowflake-access/snowflake-access.component';
import { AlertsComponent } from '@scaffold/routes/alerts/alerts.component';
import { AuthLandingComponent } from './framework/auth-landing/auth-landing.component';
import { ArchiveImportComponent } from '@scaffold/routes/archive-import/archive-import.component';

const routes: Routes = [
  { path: 'auth-landing', component: AuthLandingComponent },
  {
    path: 'sso',
    loadChildren: () => import('./sso/sso.module').then(m => m.SsoModule),
  },
  { path: 'login', component: LoginComponent },
  { path: 'direct-login', component: LoginComponent },
  { path: 'forgot-password', component: ForgotPasswordComponent },
  { path: 'new-password-required', component: NewPasswordRequiredComponent },
  { path: 'verify-phone-and-email', component: VerifyPhoneAndEmailComponent },
  // Everything in this block will need to be authenticated to navigate too
  {
    path: '',
    canActivateChild: [IsAuthenticatedGuardChild],
    children: [
      { path: '', redirectTo: '/login', pathMatch: 'full' },
      {
        path: 'admin',
        canActivate: [RolePermissionsGuard],
        data: { permission: ['view_admin'] },
        loadChildren: () =>
          import('./admin/admin.module').then(m => m.AdminModule),
      },
      { path: 'reset-password', component: ResetPasswordComponent },
      {
        path: 'account',
        loadChildren: () =>
          import('./account/account.module').then(m => m.AccountModule),
      },
      { path: 'profile', redirectTo: 'account/profile', pathMatch: 'full' },
      { path: 'settings', redirectTo: 'account/settings', pathMatch: 'full' },
      {
        path: 'documentation',
        loadChildren: () =>
          import('./documentation/documenation.module').then(
            m => m.DocumenationModule
          ),
        data: {
          toMatch: [/datax/],
          feature: ['documentation', 'datax'],
          permission: ['limit_documentation'],
        },
        canActivate: [RouteGuardService, RolePermissionsGuard],
      },
      {
        path: 'lakehouse',
        loadChildren: () =>
          import('./lakehouse/lakehouse.module').then(m => m.LakehouseModule),
        data: {
          toMatch: [/datax/],
          feature: ['lakehouse', 'datax'],
          permission: ['limit_lakehouse'],
        },
        canActivate: [RouteGuardService, RolePermissionsGuard],
      },
      // {
      //   path: "migrations",
      //   data: { toMatch: [/migration/], permission: ["view_admin"] },
      //   canActivate: [RolePermissionsGuard, RouteGuardService],
      //   loadChildren: () =>
      //     import("./migrations/migrations.module").then(
      //       (m) => m.MigrationsModule
      //     ),
      // },
      {
        path: 'imports',
        data: {
          toMatch: [/archivev2/],
          permission: ['limit_imports'],
          feature: ['archivev2', 'imports'],
          noSelfServe: false,
        },
        canActivate: [RolePermissionsGuard, RouteGuardService],
        loadChildren: () =>
          import('./migrations/migrations.module').then(
            m => m.MigrationsModule
          ),
      },
      {
        path: 'archive-import',
        component: ArchiveImportComponent,
        data: {
          toMatch: [/^archive/],
          permission: ['manage_imports'],
          feature: ['archive', 'archivev2'],
        },
        canActivate: [RolePermissionsGuard, RouteGuardService],
      },
      {
        path: 'snowflake',
        component: SnowflakeAccessComponent,
        canActivate: [RolePermissionsGuard, RouteGuardService],
        data: {
          permission: ['view_snowflake'],
          feature: ['snowflake', 'datax'],
        },
      },
      {
        path: 'analytics',
        loadChildren: () =>
          import('./analytics/analytics.module').then(m => m.AnalyticsModule),
        canActivate: [RouteGuardService, RolePermissionsGuard],
        data: {
          toMatch: [/datax/],
          feature: ['analytics', 'datax'],
          permission: ['limit_analytics'],
        },
      },
      {
        path: 'worksheets',
        loadChildren: () =>
          import('./worksheets/worksheets.module').then(
            m => m.WorksheetsModule
          ),
        data: {
          toMatch: [/datax/],
          permission: ['manage_worksheets'],
          feature: ['worksheets', 'datax'],
        },
        canActivate: [RouteGuardService, RolePermissionsGuard],
      },
      {
        path: 'data-models',
        component: ModelTypeListComponent,
        children: [
          { path: 'preferences', component: DataModelPrefsComponent },
          { path: ':id', component: ModelDetailsComponent },
          { path: ':id/:key/components', component: KeyItemsComponent },
        ],
        data: { permission: ['manage_views'] },
        canActivate: [RolePermissionsGuard],
      },
      {
        path: 'ai-detection-report',
        loadChildren: () =>
          import('./ai-detection/ai-detection.module').then(
            m => m.AiDetectionModule
          ),
        data: {
          toMatch: /ai-detection/,
          permission: ['limit_ai-detection', 'view_sigma'],
          feature: ['ai-detection'],
        },
        canActivate: [RolePermissionsGuard, RouteGuardService],
      },

      { path: 'lticallback/:id', component: LtiCallbackComponent },
      { path: 'external/:course/display', component: LtiFrameComponent },
      // { path: 'changes', component: ChangelogComponent },
      { path: 'security', component: SecurityComponent },
      {
        path: 'home',
        component: LandingPageComponent,
        canActivate: [HomeGuard],
      },

      {
        path: 'accounts',
        component: AccountSelectComponent,
        data: {
          toMatch: /archivev2/,
          feature: ['archivev2', 'sis-archive'],
          permission: ['limit_people'],
        },

        canActivate: [RouteGuardService, RolePermissionsGuard],
      },
      {
        path: 'documents',
        component: DocumentSelectComponent,
        data: {
          permission: ['sis_document_access'],
          feature: ['sis-archive'],
        },
        canActivate: [RouteGuardService, RolePermissionsGuard],
      },
      // setting up users/create to eventually work with all users/* path, assuming it will eventually require non config components there
      {
        path: 'alerts',
        component: AlertsComponent,
        data: {
          permission: ['limit_alerts'],
        },
        canActivate: [RolePermissionsGuard],
      },
      {
        path: 'courses',
        component: CourseSelectComponent,
        data: {
          permission: ['limit_courses'],
        },
        canActivate: [RolePermissionsGuard],
      },
      {
        path: 'courses/create',
        component: CourseCreateComponent,
        data: {
          permission: ['limit_courses'],
        },
        canActivate: [RolePermissionsGuard],
      },
      // {
      //   path: 'courses/:id',
      //   component: ViewFrameComponent,
      //   data: {
      //     root: true,
      //     permission: ['limit_courses'],
      //   },
      //   canDeactivate: [PendingChangesGuard],
      //   canActivate: [RolePermissionsGuard],
      //   children: [
      //     {
      //       path: ':type',
      //       component: ViewFrameComponent,
      //       canDeactivate: [PendingChangesGuard],
      //     },
      //     {
      //       path: ':type/:id',
      //       component: ViewFrameComponent,
      //       canDeactivate: [PendingChangesGuard],
      //     },
      //   ],
      // },
      // setting up users/create to eventually work with all users/* path, assuming it will eventually require non config components there
      {
        path: 'users',
        redirectTo: '/admin/users-and-roles',

        // loadChildren: () =>
        // import('./users/users.module').then(m => m.UsersModule),
        // canActivate: [RolePermissionsGuard],
      },

      {
        path: 'error',
        loadChildren: () =>
          import('./error/error.module').then(m => m.ErrorModule),
      },
      {
        path: 'views/:id',
        component: ViewFrameComponent,
        data: { allow_edit: true, root: true },
      },
      {
        path: 'views/:id/edit',
        component: EditFrameComponent,
        canDeactivate: [PendingChangesGuard],
        data: { permission: ['manage_views'] },
        canActivate: [RolePermissionsGuard],
      },
      {
        path: ':type',
        component: DataListComponent,
        data: {
          permission: ['manage_users', 'manage_tools'],
          rest_type: { manage_users: ['users', 'roles'], manage_tools: 'lti' },
        },
        canActivate: [RolePermissionsGuard],
      },
      { path: ':type/create', component: NewDataComponent },
      {
        path: ':type/:id',
        component: ViewFrameComponent,
        data: {
          root: true,
          permission: ['manage_users', 'manage_tools', 'limit_courses'],
          rest_type: {
            manage_users: ['users', 'roles'],
            manage_tools: 'lti',
            limit_courses: 'courses',
          },
        },
        canDeactivate: [PendingChangesGuard],
        canActivate: [RolePermissionsGuard],
        children: [
          {
            path: ':type',
            component: ViewFrameComponent,
            canDeactivate: [PendingChangesGuard],
          },
          {
            path: ':type/:id',
            component: ViewFrameComponent,
            canDeactivate: [PendingChangesGuard],
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      useHash: false,
      scrollPositionRestoration: 'top',
      /*, enableTracing: true */
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
